import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { TextField, useTheme } from "@mui/material";
import { styled, Body3, helperTextHeight, inputHeight, muiInputFormsCSS } from "@iventis/styles";

import { blankCodeAsArray, numberInputAcceptedSymbols } from "@iventis/utilities";
import { Theme } from "@emotion/react";

const keyCodes = {
    backspace: "Backspace",
    arrowLeft: "ArrowLeft",
    arrowRight: "ArrowRight",
};

/**
 * Verification code input composed of multiple single digit input boxes.
 */
export const VerificationCodeInput = forwardRef<
    { updateCode(code: string[]): void },
    {
        updateVerificationCode: (code: string) => void;
        /** Number of input boxes */
        length: number;
        error?: boolean;
        errorText?: string;
        disabled?: boolean;
    }
>(({ updateVerificationCode, length, error, errorText, disabled }, ref) => {
    const inputControlRef = useRef<HTMLDivElement | null>(null);

    const theme = useTheme<Theme>();

    /** Code represented as tuple */
    const [codeInputValues, setCodeInputValues] = useState<string[]>(blankCodeAsArray(length));

    /** Replaces the matching index for the input with the inputs new value */
    const editInputValue = (target: HTMLInputElement) => setCodeInputValues((code) => code.map((s, index) => (index === Number(target.id) ? target.value : s)));

    /** Tuple converted into a string (variable length) */
    const codeAsString = codeInputValues.join("");

    useImperativeHandle(ref, () => ({
        updateCode(code) {
            setCodeInputValues(code);
        },
    }));

    /** Send updated code back to parent each time it updates */
    useEffect(() => {
        updateVerificationCode(codeAsString);
    }, [codeAsString]);

    /** Given an input, gets the previous input */
    const getPrevInput = (target: HTMLInputElement) => {
        const inputs = inputControlRef.current.getElementsByTagName("input");
        return inputs[Number(target.id) - 1];
    };

    /** Given an input, gets the next input */
    const getNextInput = (target: HTMLInputElement) => {
        const inputs = inputControlRef.current.getElementsByTagName("input");
        return inputs[Number(target.id) + 1];
    };

    /** When an input has been inputted, moves focus to the next input */
    const onInput = (e: React.FormEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        const inputs = inputControlRef.current.getElementsByTagName("input");
        const nextInput = inputs[Number(input.id) + 1];
        if (nextInput && input.value) {
            nextInput.focus();
            if (nextInput.value) {
                nextInput.select();
            }
        }
    };

    /** If the paste has correct length, replace entire code */
    const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const paste = e.clipboardData.getData("text")?.split("");
        if (paste.length === length && paste.every((n) => Number.isInteger(Number(n)))) {
            setCodeInputValues(paste);
        }
    };

    /** If current input has a value, backspace should delete it; if not, move focus back and delete */
    const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        if (input.value?.length > 0) {
            editInputValue(input);
            return;
        }
        const prevInput = getPrevInput(input);
        if (prevInput) {
            prevInput.focus();
        }
    };

    /** On arrow left, move focus to previous input */
    const handleArrowLeft = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const prevInput = getPrevInput(e.currentTarget);
        if (!prevInput) return;
        prevInput.focus();
    };

    /** On arrow right, move focus to next input */
    const handleArrowRight = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const nextInput = getNextInput(e.currentTarget);
        if (!nextInput) return;
        nextInput.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (numberInputAcceptedSymbols.includes(e.key)) {
            /** Prevent any non-integer symbols being inputted */
            e.preventDefault();
            return;
        }
        switch (e.key) {
            case keyCodes.backspace:
                handleBackspace(e);
                break;
            case keyCodes.arrowLeft:
                handleArrowLeft(e);
                break;
            case keyCodes.arrowRight:
                handleArrowRight(e);
                break;
            default:
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value?.length > 1) {
            // Cannot input more than one digit per box (Mui TextField number inputs can't handle maxLength property)
            e.preventDefault();
            return;
        }
        editInputValue(e.currentTarget);
    };

    return (
        <>
            <StyledInputControl ref={inputControlRef}>
                {codeInputValues.map((value, index) => (
                    <TextField
                        // eslint-disable-next-line react/no-array-index-key
                        key={`code-${index}`} // This is allowed because the index is what makes it unique in this case
                        value={value}
                        onChange={onChange}
                        type="number"
                        inputProps={{ onKeyDown, onFocus: (e) => e.target.select(), onPaste, onInput, id: index.toString() }}
                        error={error}
                        disabled={disabled}
                    />
                ))}
            </StyledInputControl>
            {error && <Body3 style={{ color: theme.toastColours.error, height: helperTextHeight, lineHeight: helperTextHeight }}>{errorText}</Body3>}
        </>
    );
});

const StyledInputControl = styled.div`
    display: flex;
    gap: 10px;
    ${muiInputFormsCSS}
    .MuiOutlinedInput-root {
        width: ${inputHeight};
    }
    .MuiInputBase-input {
        width: 1ch;
    }

    /* Removes the native number incrementor */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        /* Chrome, Safari, Edge, Opera */
        -webkit-appearance: none;
        margin: 0;
    }
    input[type="number"] {
        /* Firefox */
        -moz-appearance: textfield;
    }
`;
